// @givmo/design-system — ModuleGrid + ModuleSquare (the console-as-home app grid, EP-SUITE3;
// shell contract v3 at OM-14; the popup's two registers at OM-34).
//
// The "app-store square" language the Givmo suite is organized around: an org console's app list is
// a grid of squares, and every portal's chrome carries a waffle popping the SAME squares (see
// ./app-switcher.tsx). One component serves both so the two can never render a different set.
//
// PURELY PRESENTATIONAL, deliberately. It takes `modules: ModuleSquareProps[]` and renders them; it
// resolves no URLs, reads no flags, and holds no permission model. Which squares a caller is entitled
// to see, and which affordance each carries, is answered by the app composing the array.
//
// EVERY SQUARE IS A REAL DESTINATION OR SAYS PLAINLY WHY IT IS NOT. There is no disabled state: a
// module that is not built, or that the organization's class does not carry, is simply ABSENT from
// the array. Five rendered states remain:
//
//   link   — an ordinary square.
//   locked — the caller can SEE the module but may not open it. A non-link carrying the lock glyph
//            and "You do not have access", plus — when the composing app supplies `onRequestAccess`
//            — a real "Request access" link that opens the app's request dialog (R4/R9).
//   off    — the organization has the module switched off. Two shapes, decided by the composing app:
//            with `turnOn` it is a real link into the settings that flip it, carrying "Turn on" (for
//            a holder of the switch's capability); without, it reads exactly as a locked square does
//            for everyone else — the same sentence, the same optional request link. ONE sentence for
//            both, as ruled (D-CPO-31); the difference between the two is which door is offered.
//   setting-up — a provisioning hook is still working. The same actionability split applies: a
//                person who can manage setup gets a real link to that surface; everyone else gets the
//                clear non-link statement "Setting up".
//   coming-soon — OM-33: a module Givmo has announced to the organization's class that THIS
//                 organization is not offered yet. A muted non-link saying exactly "Coming soon" —
//                 no link, no request door, no lock (it is not about the person's access) and, like
//                 every square, no menu.
//
// A locked/off square may additionally carry the caller's OWN REQUEST fact as a sub-state (shell
// contract v3, T5.1): PENDING reads "Access requested" with no link; DECLINED reads ONE clearly-a-link
// line, "Request declined", opening a popover with the decliner's note and a "Request access" action
// (D4). The verdict precedence is unchanged — the request fact decorates, it never reorders.
//
// Nothing here renders a control that does nothing when clicked, and the union below makes that a
// COMPILE error rather than something a reviewer has to notice: an unlocked square must carry an
// `href`, an off square carrying the "Turn on" action must carry one, and an off square without the
// action must not. The request link renders ONLY when the callback exists — the honesty law.
//
// `current` marks the square for the app the caller is already in. It stays a link (clicking it is a
// real, correct action — it reloads the app you are on) and carries `aria-current="page"`, so the grid
// reads as the same stable set of apps from inside any of them.
//
// ⚠ A SQUARE MAY POINT AT THIS SAME BUILD, and the seam for that is ONE optional callback (OM-12). A
// host marks such a square `inApp` and passes `onNavigate`; the anchor KEEPS its `href` (copy-address
// and new-tab keep working, and a host that passes nothing behaves exactly as before), and only the
// ordinary left-click is handed over.
//
// ══ THREE DENSITIES, ONE READING (OM-34, direction C "Two registers", RULED 2026-10-01) ══════════
//
// What a square SAYS and where it GOES are decided once, below; a density decides only how it LOOKS.
//
//   full     — the legacy single grid: icon tile, name, optional description, status lines.
//   launcher — the popup's FIRST register, "My Givmo Apps": a blue-tinted icon square over the name.
//              The current app is marked by its icon's stronger tint and a fine ring alone (never a
//              filled box — that pointed at where you already are); the Admin Console's count sits
//              on the icon's corner. Blue is spent here, on what you can open.
//   shelf    — the popup's SECOND register, on a sunken shelf below the line: a compact ROW — an
//              outlined 32px glyph (the lock on its corner where the row is about access), the name
//              and ONE status line carrying its door. Two to a row on a phone, three from `md`.
//
// ⚠ THERE IS NO TILE OPTIONS MENU (RULED 2026-10-01 07:02 PDT, amending OM-14 T3): "Set as default
// landing page" lives in each person's own preferences (the settings page's Landing card), so no
// square carries a kebab — on a phone or at desktop.
//
// DATA CONTRACT: `module-grid` (the grid root, caller-supplied via `testId`) and
// `module-square-<slug>` per tile, derived from the slug so a caller cannot forget one; plus
// `module-square-<slug>-request`, `-requested`, `-declined`, `-declined-popover`,
// `-declined-request`, `-no-access`, `-setting-up`, `-coming-soon`, `-badge`.
import type { MouseEvent, ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { shouldInterceptClick } from '../lib/navigation';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Badge } from './badge';
import { Button } from './button';
import { Icon } from './icon';
import { Popover } from './popover';

/** The caller's own request fact on a locked/off square — a SUB-STATE, never another state. */
export interface ModuleSquareAccessRequest {
  status: 'pending' | 'declined';
  /** The decliner's note, shown in the declined popover. Absent or blank ⇒ "No reason was given." */
  note?: string | null;
}

/** The fields every square carries, whatever its shape. */
interface ModuleSquareBase {
  /**
   * Stable module identifier (`employer`, `org`, `books`…). Drives the React key and the
   * `module-square-<slug>` testid, so it must be URL/selector-safe and must not change once a
   * program's square ships — an e2e selector keys on it.
   */
  slug: string;
  /** The module's name, as a person would say it. One or two words. */
  label: string;
  /** One short line about what the module is for. Optional — omit rather than pad. Full density only. */
  description?: string;
  /** The module's glyph (a rendered `<Icon>`), shown in the square's icon tile. */
  icon?: ReactNode;
}

/**
 * How a square renders. Omitted reads as `link`, so the ordinary case needs no word.
 *
 * These are the rendered states; the remaining one, "absent", has no member here because an absent
 * module is not in the array at all.
 */
export type ModuleSquareState = 'link' | 'locked' | 'off' | 'setting-up' | 'coming-soon';

/**
 * A square that GOES somewhere.
 *
 * `href` is REQUIRED, and that is the point of the union below: a link square without a destination
 * would render a tile that looks live and does nothing when clicked — the exact decorative control
 * this system refuses. Making it a type error is stronger than remembering.
 */
interface NavigableModuleSquare extends ModuleSquareBase {
  state?: 'link';
  /**
   * Where the square goes. Absolute for a cross-origin module (the usual case — each portal is its
   * own deployed host), or an in-app path.
   */
  href: string;
  /** The caller is already in this module: still a link, marked `aria-current="page"`. */
  current?: boolean;
  /**
   * This build SERVES that destination itself, so a host with a router may follow it in-app.
   *
   * ⚠ THE HOST DECIDES THIS, and nothing here inspects the `href` to guess. Whether a path belongs
   * to this build is a fact about the build's routes and its configured module origins — which the
   * composing app knows and this component must not re-derive. Absent (the default) means a plain
   * document navigation, which is what every cross-origin module gets.
   */
  inApp?: boolean;
  /**
   * The organization's own management tile (the Admin Console), not an app. Renders like a link
   * square and may carry a count badge (pending requests).
   */
  adminConsole?: boolean;
  /** A count over the icon — the Admin Console tile's pending-requests badge. Omitted at zero. */
  badge?: number;
  turnOn?: never;
  viewSetup?: never;
  onRequestAccess?: never;
  accessRequest?: never;
}

/**
 * A square the caller can SEE but may not open. Renders as a NON-link carrying the lock glyph and
 * "You do not have access" — never a disabled-looking link, and never a silent no-op click. It takes
 * no `href`, because there is nowhere it may send them; it takes an optional request door.
 */
interface LockedModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'locked';
  href?: never;
  current?: never;
  inApp?: never;
  turnOn?: never;
  viewSetup?: never;
  /** Opens the host's request dialog. ABSENT ⇒ no "Request access" link renders (the honesty law). */
  onRequestAccess?: () => void;
  /** The caller's own request fact, when the standing carried one. */
  accessRequest?: ModuleSquareAccessRequest;
}

/**
 * Switched off for the organization, AND this caller can do something about it.
 *
 * `href` is REQUIRED for the same reason a link square's is: "Turn on" is an affordance, so it must
 * open the surface that actually flips the switch. An off square that offered the words without the
 * destination would be the decorative control in its most misleading form — a person pressing it and
 * nothing happening, on a control that named the act.
 */
interface OffActionableModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'off';
  turnOn: true;
  href: string;
  current?: never;
  /**
   * ⚠ FORBIDDEN, and deliberately so. This destination is not a module — it is the organization's
   * own settings door, composed by the host's own rule rather than by the module resolver, so it is
   * not part of the in-app seam and must not quietly join it.
   */
  inApp?: never;
  viewSetup?: never;
  onRequestAccess?: never;
  accessRequest?: never;
}

/**
 * Switched off for the organization, and this caller cannot flip the switch.
 *
 * `href` is FORBIDDEN — the same compile-time honesty `locked` has. It reads exactly as a locked
 * square does ("You do not have access", the lock glyph, the optional request door — D-CPO-31, one
 * sentence for both), because from this person's side the answer is the same: they cannot open it,
 * and asking is the one act available to them.
 */
interface OffStatedModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'off';
  turnOn?: never;
  href?: never;
  current?: never;
  inApp?: never;
  viewSetup?: never;
  onRequestAccess?: () => void;
  accessRequest?: ModuleSquareAccessRequest;
}

/**
 * Setup is in progress, and this caller may open the management surface that owns it.
 *
 * `href` is required: the whole square is a real link. `viewSetup` is the discriminant rather than
 * reusing `turnOn`, because the hook has already started and telling the type otherwise would make a
 * later caller render the wrong words.
 */
interface SettingUpActionableModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'setting-up';
  viewSetup: true;
  href: string;
  current?: never;
  inApp?: never;
  turnOn?: never;
  onRequestAccess?: never;
  accessRequest?: never;
}

/** Setup is in progress, but this caller has no management action. A clear non-link statement. */
interface SettingUpStatedModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'setting-up';
  viewSetup?: never;
  href?: never;
  current?: never;
  inApp?: never;
  turnOn?: never;
  onRequestAccess?: never;
  accessRequest?: never;
}

/**
 * OM-33 — announced to the organization's class, not offered to this organization. A muted
 * statement and nothing else: no destination, no request door (there is no one here to ask), and
 * no lock — it says nothing about the person's access.
 */
interface ComingSoonModuleSquare extends ModuleSquareBase {
  adminConsole?: never;
  badge?: never;
  state: 'coming-soon';
  href?: never;
  current?: never;
  inApp?: never;
  turnOn?: never;
  viewSetup?: never;
  onRequestAccess?: never;
  accessRequest?: never;
}

/**
 * One square. A DISCRIMINATED UNION on `state` (and, inside `off`, on `turnOn`), so the compiler —
 * not a reviewer — rejects the shapes that would be lies: a link with no destination, an off tile
 * that names an act it cannot perform, an off tile that carries a destination it never offers, and a
 * coming-soon tile that goes anywhere or asks for anything.
 */
export type ModuleSquareProps =
  | NavigableModuleSquare
  | LockedModuleSquare
  | OffActionableModuleSquare
  | OffStatedModuleSquare
  | SettingUpActionableModuleSquare
  | SettingUpStatedModuleSquare
  | ComingSoonModuleSquare;

/**
 * Follow an `inApp` square's destination without a document load, when the host can.
 *
 * ⚠ ONLY squares the host marked `inApp` reach this, and the anchor keeps its `href` either way.
 * A host that passes nothing gets the plain-anchor behaviour every square has always had.
 */
export type ModuleNavigate = (href: string) => void;

/** How a grid's squares LOOK — see the header. Never what they say or where they go. */
export type ModuleGridDensity = 'full' | 'launcher' | 'shelf';

export interface ModuleGridProps extends TestIdProps {
  /** The squares to render, in display order. An empty array renders an empty grid, not a message. */
  modules: readonly ModuleSquareProps[];
  /**
   * The grid's DESKTOP column count at `full` density — a maximum, not a fixed number. 3 (the
   * waffle shape) by default.
   *
   * ⚠ IT STEPS DOWN, and reading it as fixed is what made a four-up grid collide its own labels at
   * 390px. See {@link COLUMN_CLASS}.
   */
  columns?: 2 | 3 | 4;
  /**
   * The popup's registers (OM-34): `launcher` (the first section) and `shelf` (the second), each
   * THREE per row from the tablet breakpoint and TWO below it, whatever `columns` says. `full`
   * (the default) is the legacy grid.
   */
  density?: ModuleGridDensity;
  /** In-app navigation for `inApp` squares — see {@link ModuleNavigate}. */
  onNavigate?: ModuleNavigate;
  className?: string;
  /** Accessible name for the grid's list (e.g. "Your Givmo apps"). */
  'aria-label'?: string;
  /** Hide an EMPTY list from layout and assistive technology while keeping it in the DOM. */
  hidden?: boolean;
}

/**
 * `columns` is the DESKTOP MAXIMUM, and every count steps down to 2 on a phone and 3 on a tablet
 * (OM-13, ruled 2026-08-31).
 *
 * ⚠ READING IT AS A FIXED COUNT WAS A REAL DEFECT, not a preference: a four-up grid at 390px gave
 * each square ~85px, so "Company console" painted straight over "Employer" and every description
 * wrapped to three lines. A tile whose label leaves its tile is the failure, and it is invisible on
 * the desktop the count was chosen for.
 *
 * The steps are `sm` (40rem) and `lg` (64rem) — the estate's content and layout breakpoints, in that
 * order. A count already at or below a step simply stays there, so `columns={2}` is 2 everywhere.
 *
 * Tailwind cannot see a computed class name, so the variants are spelled out.
 */
const COLUMN_CLASS: Record<NonNullable<ModuleGridProps['columns']>, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
};

/**
 * The popup's registers (D6, OM-34): two per row on a phone, three from the tablet breakpoint — and
 * FEWER, wider columns once a larger root text size would truncate a name. No column is narrower
 * than `calc(58px + 4.25rem)`: a shelf row's fixed chrome (its padding, the 32px glyph and its gap)
 * plus room for the longest module name at its 13px ("Compliance" is ~4.1rem). Auto-fill drops a
 * column before a name would truncate; the `max()` caps the count at the breakpoint's, so at a 100%
 * root text size, where the minimum is 126px, every width from 314px up lays out exactly as before.
 * The 0.01px keeps the cap from rounding a column away. Both registers share it, so they reflow
 * together; the count rides `--popup-columns` because Tailwind cannot see a computed class name.
 */
const POPUP_COLUMN_CLASS =
  '[--popup-columns:2] md:[--popup-columns:3] grid-cols-[repeat(auto-fill,minmax(max(min(calc(58px_+_4.25rem),100%),calc((100%_-_(var(--popup-columns)_-_1)_*_var(--spacing-4))_/_var(--popup-columns)_-_0.01px)),1fr))]';

const GRID_CLASS: Record<
  ModuleGridDensity,
  (columns: NonNullable<ModuleGridProps['columns']>) => string
> = {
  full: (columns) => cn('gap-8', COLUMN_CLASS[columns]),
  launcher: () => cn('gap-4', POPUP_COLUMN_CLASS),
  shelf: () => cn('gap-x-4 gap-y-2', POPUP_COLUMN_CLASS),
};

// ⚠ `min-w-0` IS LOAD-BEARING, not tidiness. A square is a grid item, and a grid item's automatic
// minimum size is its MIN-CONTENT width — which for a `truncate` label (`white-space: nowrap`) is the
// whole label. Without this the tile grows past its track and the label paints over the next square,
// with `truncate` never clipping anything: exactly what "Company console" did over "Employer" at
// 390px. The label's own wrapper carries `w-full` for the other half of the same fix — `truncate`
// clips only against a bounded width, and `items-center` alone leaves it content-sized.
const SQUARE_BASE =
  'group flex h-full min-w-0 flex-col items-center gap-8 rounded-xl border border-transparent p-12 text-center outline-none transition-colors';

/** The launcher square: an icon square over the name, the whole square the target. */
const LAUNCHER_BASE =
  'group relative flex h-full min-w-0 flex-col items-center gap-8 rounded-xl px-6 pb-12 pt-14 text-center outline-none transition-colors';

/**
 * The shelf row: glyph · name · one status line, left-aligned, the 48px row the phone floor needs.
 * Its OWN height, never the grid row's (no `h-full`): beside a row whose sentence wraps onto more
 * lines, a one-line row stays at the top of the grid row, so the names read on one line across it.
 */
const SHELF_BASE =
  'group relative flex min-h-[48px] min-w-0 items-center gap-10 rounded-lg px-8 py-6 text-left outline-none transition-colors';

/**
 * The lines a non-link/actionable-state square can carry, pinned as copy.
 *
 * Exported because they are a CONTRACT: the hosts' own copy tests assert them, and the sentence a
 * person reads is the whole difference between the states.
 *
 * ⚠ D-CPO-31 (2026-09-02): the locked line and the off line are ONE sentence now — "You do not have
 * access" — replacing "Ask your admin" and "Not enabled". Both names survive so every consumer keeps
 * compiling and the two states stay distinguishable in code; their VALUES are deliberately equal.
 */
export const MODULE_SQUARE_NO_ACCESS_LINE = 'You do not have access';
export const MODULE_SQUARE_LOCKED_LINE = MODULE_SQUARE_NO_ACCESS_LINE;
export const MODULE_SQUARE_OFF_LINE = MODULE_SQUARE_NO_ACCESS_LINE;
export const MODULE_SQUARE_TURN_ON_LINE = 'Turn on';
export const MODULE_SQUARE_SETTING_UP_LINE = 'Setting up';
/** The request door (R9: never "…from global admin" — the request also goes to the app's Owners). */
export const MODULE_SQUARE_REQUEST_LINE = 'Request access';
/** The pending sub-state: a statement, no link. */
export const MODULE_SQUARE_REQUESTED_LINE = 'Access requested';
/** The declined sub-state: ONE clearly-a-link line (D4). */
export const MODULE_SQUARE_DECLINED_LINE = 'Request declined';
/** The declined popover's heading and its fallback body. */
export const MODULE_SQUARE_DECLINED_WHY = 'Why it was declined';
export const MODULE_SQUARE_DECLINED_NO_NOTE = 'No reason was given.';
/** OM-33 — the announced module's sentence, exactly (Jonathan 2026-10-01: no exclamation mark). */
export const MODULE_SQUARE_COMING_SOON_LINE = 'Coming soon';

/** One square, plus the host's optional in-app navigation and the grid's density. */
export type ModuleSquareComponentProps = ModuleSquareProps & {
  onNavigate?: ModuleNavigate;
  density?: ModuleGridDensity;
};

/**
 * The 44px phone floor on a no-access square's ONE affordance. Below `lg` the link's hit area is
 * the WHOLE tile (a pseudo-element over the tile's relative box — the tile has no other control),
 * while the visible text stays an 18px line; at desktop the pointer's precision needs nothing.
 */
const HIT_AREA = 'max-lg:after:absolute max-lg:after:inset-0';

/** How a status line reads at each density: its base, its quiet tone, its blue tone, its door. */
interface LineRecipe {
  text: string;
  muted: string;
  brand: string;
  link: string;
}

const SQUARE_LINE: LineRecipe = {
  text: 'font-sans text-s12 tracking-givmo',
  muted: 'font-w500 text-text-tertiary',
  brand: 'font-w500 text-brand-600 group-hover:text-brand-700',
  link: 'rounded-sm font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700',
};

// ⚠ A shelf line WRAPS onto as many lines as it needs: never truncated, never clamped. Its column
// is ~110px of text at desktop, and the doorless no-access row says the whole pinned sentence —
// "You do not have access" (the employer portal's locked rows, which carry no request door, and a
// holder of a switched-off app) — which truncation cut to "You do not have acc…", and a two-line
// clamp cut at a 200% root text size, where it needs three. Every other line fits on one at 100%.
const SHELF_LINE: LineRecipe = {
  text: 'text-left font-sans text-s12 leading-h13 tracking-givmo',
  muted: 'font-w400 text-text-tertiary',
  brand: 'font-w600 text-brand-600 group-hover:text-brand-700',
  link: 'w-fit rounded-sm font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700',
};

/**
 * The declined sub-state (D4): ONE clearly-a-link line opening the note and the way to ask again.
 */
function DeclinedLine({
  testId,
  label,
  note,
  onRequestAccess,
  recipe,
}: {
  testId: string;
  label: string;
  note: string | null | undefined;
  onRequestAccess: (() => void) | undefined;
  recipe: LineRecipe;
}) {
  const text = (note ?? '').trim();
  return (
    <Popover.Root>
      <Popover.Trigger
        className={cn(recipe.text, recipe.link, FOCUS_RING, HIT_AREA)}
        {...testIdProp(`${testId}-declined`)}
      >
        {MODULE_SQUARE_DECLINED_LINE}
      </Popover.Trigger>
      <Popover.Content
        saas
        side="bottom"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        className="w-[280px] p-14 shadow-lg max-sm:w-[calc(100vw-24px)]"
        aria-label={`Your request to open ${label} was declined`}
        testId={`${testId}-declined-popover`}
      >
        <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
          {MODULE_SQUARE_DECLINED_WHY}
        </p>
        <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {text.length > 0 ? `“${text}”` : MODULE_SQUARE_DECLINED_NO_NOTE}
        </p>
        {onRequestAccess !== undefined && (
          <Popover.Close
            render={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="mt-12"
                onClick={onRequestAccess}
                testId={`${testId}-declined-request`}
              />
            }
          >
            {MODULE_SQUARE_REQUEST_LINE}
          </Popover.Close>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

/** What a square says and where it goes, decided ONCE for every density. */
interface SquareReading {
  href: string | undefined;
  current: boolean;
  inApp: boolean;
  badge: number | undefined;
  /** "Not now": locked, off, setting-up or coming soon — the quiet treatment. */
  muted: boolean;
  /** The no-access reading (R4's lock): locked, or off for someone who cannot flip it. */
  lock: boolean;
}

function readSquare(props: ModuleSquareProps): SquareReading {
  const state: ModuleSquareState = props.state ?? 'link';
  const off = state === 'off';
  const turnOn = off && props.turnOn === true;
  const viewSetup = state === 'setting-up' && props.viewSetup === true;
  return {
    // Only a link square and an off/setting-up square carrying the action have a destination; the
    // union already forbids the rest, and this reads the cases that do rather than negating the rest.
    href: state === 'link' || turnOn || viewSetup ? props.href : undefined,
    current: state === 'link' && props.current === true,
    inApp: state === 'link' && props.inApp === true,
    badge: state === 'link' ? props.badge : undefined,
    muted: state !== 'link',
    lock: state === 'locked' || (off && !turnOn),
  };
}

/** The ONE status line a square carries (at most two at `full` density), or `null` for a link. */
function StatusLines({
  props,
  testId,
  recipe,
  oneLine,
}: {
  props: ModuleSquareProps;
  testId: string;
  recipe: LineRecipe;
  /** The popup's densities hold ONE line; the legacy full square may hold the sentence AND the door. */
  oneLine: boolean;
}) {
  const state: ModuleSquareState = props.state ?? 'link';
  const { label } = props;
  if (state === 'coming-soon') {
    return (
      <span className={cn(recipe.text, recipe.muted)} {...testIdProp(`${testId}-coming-soon`)}>
        {MODULE_SQUARE_COMING_SOON_LINE}
      </span>
    );
  }
  const off = state === 'off';
  const turnOn = off && props.turnOn === true;
  const noAccess = state === 'locked' || (off && !turnOn);
  if (noAccess) {
    const request = props.accessRequest;
    const onRequestAccess = props.onRequestAccess;
    if (request?.status === 'pending') {
      return (
        <span className={cn(recipe.text, recipe.muted)} {...testIdProp(`${testId}-requested`)}>
          {MODULE_SQUARE_REQUESTED_LINE}
        </span>
      );
    }
    if (request?.status === 'declined') {
      return (
        <DeclinedLine
          testId={testId}
          label={label}
          note={request.note}
          onRequestAccess={onRequestAccess}
          recipe={recipe}
        />
      );
    }
    const door =
      onRequestAccess === undefined ? null : (
        <button
          type="button"
          onClick={onRequestAccess}
          className={cn(recipe.text, recipe.link, FOCUS_RING, HIT_AREA)}
          // The ONE line is the door; the lock badge carries the no-access meaning and the
          // sentence rides the accessible name — the confirm render's standing judgment call.
          aria-label={
            oneLine
              ? `${label} — ${MODULE_SQUARE_NO_ACCESS_LINE}. ${MODULE_SQUARE_REQUEST_LINE}.`
              : undefined
          }
          {...testIdProp(`${testId}-request`)}
        >
          {MODULE_SQUARE_REQUEST_LINE}
        </button>
      );
    const sentence = (
      <span className={cn(recipe.text, recipe.muted)} {...testIdProp(`${testId}-no-access`)}>
        {MODULE_SQUARE_NO_ACCESS_LINE}
      </span>
    );
    if (oneLine) return door ?? sentence;
    return (
      <>
        {sentence}
        {/* The request door renders ONLY when the host wired one — the honesty law. */}
        {door}
      </>
    );
  }
  if (turnOn) {
    return <span className={cn(recipe.text, recipe.brand)}>{MODULE_SQUARE_TURN_ON_LINE}</span>;
  }
  if (state === 'setting-up') {
    // The same exact sentence in both variants. Actionability is expressed by whether the whole
    // square is a real anchor, never by drawing a button that can be inert.
    return (
      <span
        className={cn(recipe.text, props.viewSetup === true ? recipe.brand : recipe.muted)}
        {...testIdProp(`${testId}-setting-up`)}
      >
        {MODULE_SQUARE_SETTING_UP_LINE}
      </span>
    );
  }
  return null;
}

/** The lock badge over an icon's corner — R4's "a little lock icon over it". */
function LockBadge({ size }: { size: 'square' | 'row' }) {
  return size === 'row' ? (
    <span
      className="absolute -bottom-4 -right-4 flex size-16 items-center justify-center rounded-full bg-surface-sunken text-icon"
      aria-hidden
    >
      <Icon icon={Lock} size={10} aria-hidden />
    </span>
  ) : (
    // On the tile's corner, on the surface, so it reads as a state of the app rather than as a
    // second glyph beside it.
    <span
      className="absolute -bottom-4 -right-4 flex size-20 items-center justify-center rounded-full border border-border-subtle bg-surface text-icon"
      aria-hidden
    >
      <Icon icon={Lock} size={11} aria-hidden />
    </span>
  );
}

/**
 * The count over an icon's corner — the Admin Console's pending requests.
 *
 * Sized in rem, not by the chip's `sm` box alone: `sm` pins a FIXED 20px height and 6px padding
 * beside rem text, so at a larger root text size the digit outgrew its chip and sat half on the
 * icon. Here the chip's height floor, its padding and its corner offsets grow with the text; at a
 * 100% root they are exactly `sm`'s 20px and 6px and the corner's 10px (8px unringed) and 6px, so
 * the default renders pixel-identical. Overridden HERE rather than in `sm`: the ruling covers this
 * chip, and every other `sm` chip keeps the box its callers were measured against.
 */
function CountBadge({ count, testId, ringed }: { count: number; testId: string; ringed: boolean }) {
  return (
    <span
      className={cn('absolute -top-[0.375rem]', ringed ? '-right-[0.625rem]' : '-right-[0.5rem]')}
      {...testIdProp(`${testId}-badge`)}
    >
      <Badge
        tone="brand"
        size="sm"
        className={cn(
          'h-auto min-h-[1.25rem] px-[0.375rem]',
          // On a blue-tinted icon the chip needs the surface between them, or it melts into it.
          ringed && 'ring-2 ring-surface',
        )}
        aria-label={`${count} waiting`}
      >
        {count}
      </Badge>
    </span>
  );
}

/** The icon square, per density. */
function SquareIcon({
  props,
  reading,
  density,
  testId,
}: {
  props: ModuleSquareProps;
  reading: SquareReading;
  density: ModuleGridDensity;
  testId: string;
}) {
  const { icon } = props;
  const { muted, current, lock, badge } = reading;
  if (density === 'shelf') {
    return (
      <span className="relative shrink-0">
        <span className="flex size-32 items-center justify-center rounded-lg border border-border bg-surface text-icon-muted [&_svg]:size-16">
          {icon}
        </span>
        {lock && <LockBadge size="row" />}
      </span>
    );
  }
  // The LAUNCHER square, its radius and its glyph are sized in rem — at a 100% root exactly the
  // 44px / 12px / 20px they always were — so the square grows with the text around it and the count
  // chip (rem too, `CountBadge`) keeps its place on the square's shoulder at every text size. The
  // shelf's glyph box and the legacy `full` square stay in px.
  const tile =
    density === 'launcher'
      ? cn(
          'flex size-[2.75rem] items-center justify-center rounded-[0.75rem] transition-colors [&_svg]:size-[1.25rem]',
          muted
            ? 'border border-border bg-surface text-icon-muted'
            : current
              ? 'bg-brand-100 text-brand-700 ring-1 ring-inset ring-brand-200'
              : 'bg-brand-50 text-brand-600 group-hover:bg-brand-100 group-hover:text-brand-700',
        )
      : cn(
          'flex size-44 items-center justify-center rounded-xl transition-colors',
          muted
            ? 'bg-neutral-100 text-icon-muted'
            : current
              ? 'bg-brand-100 text-brand-700'
              : 'bg-neutral-100 text-icon group-hover:bg-brand-100 group-hover:text-brand-700',
        );
  return (
    <span className="relative">
      <span className={tile}>{icon}</span>
      {lock && <LockBadge size="square" />}
      {badge !== undefined && badge > 0 && (
        <CountBadge count={badge} testId={testId} ringed={density === 'launcher'} />
      )}
    </span>
  );
}

export function ModuleSquare(props: ModuleSquareComponentProps) {
  const { slug, label, description, onNavigate, density = 'full' } = props;
  const reading = readSquare(props);
  const { href, current, inApp, muted } = reading;
  const testId = `module-square-${slug}`;
  const iconNode = <SquareIcon props={props} reading={reading} density={density} testId={testId} />;

  let base: string;
  let body: ReactNode;
  if (density === 'shelf') {
    base = SHELF_BASE;
    body = (
      <>
        {iconNode}
        <span className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="truncate font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
            {label}
          </span>
          <StatusLines props={props} testId={testId} recipe={SHELF_LINE} oneLine />
        </span>
      </>
    );
  } else if (density === 'launcher') {
    base = LAUNCHER_BASE;
    body = (
      <>
        {iconNode}
        <span
          className={cn(
            'w-full truncate font-sans text-s13 tracking-givmo',
            current
              ? 'font-w600 text-brand-700'
              : muted
                ? 'font-w500 text-text-secondary'
                : 'font-w500 text-text-strong',
          )}
        >
          {label}
        </span>
        <StatusLines props={props} testId={testId} recipe={SQUARE_LINE} oneLine />
      </>
    );
  } else {
    base = SQUARE_BASE;
    body = (
      <>
        {iconNode}
        <span className="flex w-full min-w-0 flex-col gap-2">
          <span
            className={cn(
              'w-full truncate font-sans text-s13 tracking-givmo',
              // ⚠ 600, NOT 700. Role discipline: 700 is the rare heavy display, and a 13px tile label
              // is not one — the tinted fill, the border, `text-brand-700` and `aria-current` already
              // carry "you are here", so the extra weight bought emphasis the state did not need.
              current ? 'font-w600 text-brand-700' : 'font-w600 text-text-strong',
              muted && 'text-text-tertiary',
            )}
          >
            {label}
          </span>
          {description !== undefined && (
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {description}
            </span>
          )}
          <StatusLines props={props} testId={testId} recipe={SQUARE_LINE} oneLine={false} />
        </span>
      </>
    );
  }

  // `href === undefined` is unreachable for every actionable shape (the union forbids it), but the
  // runtime check stays for JS callers and for a `props`-spread that outran the types.
  if (href === undefined) {
    return (
      <li className="min-w-0">
        <div className={cn(base, 'relative cursor-default')} {...testIdProp(testId)}>
          {body}
        </div>
      </li>
    );
  }

  // ⚠ The interception is narrow on purpose: an `inApp` square whose host supplied no navigation,
  // and every gesture `shouldInterceptClick` refuses, fall through to the anchor untouched.
  const handleClick =
    inApp && onNavigate !== undefined
      ? (event: MouseEvent<HTMLAnchorElement>) => {
          if (!shouldInterceptClick(event)) return;
          event.preventDefault();
          onNavigate(href);
        }
      : undefined;

  const hover =
    density === 'shelf'
      ? 'hover:bg-surface'
      : density === 'launcher'
        ? 'hover:bg-surface-hover'
        : 'hover:border-border-subtle hover:bg-surface-hover';

  return (
    <li className="min-w-0">
      <a
        href={href}
        onClick={handleClick}
        aria-current={current ? 'page' : undefined}
        className={cn(
          base,
          hover,
          FOCUS_RING,
          density === 'full' && current && 'border-border-subtle bg-surface-active',
        )}
        {...testIdProp(testId)}
      >
        {body}
      </a>
    </li>
  );
}

export function ModuleGrid({
  modules,
  columns = 3,
  density = 'full',
  onNavigate,
  className,
  testId,
  hidden,
  'aria-label': ariaLabel,
}: ModuleGridProps) {
  return (
    <ul
      className={cn('grid', GRID_CLASS[density](columns), className)}
      aria-label={ariaLabel}
      hidden={hidden}
      {...testIdProp(testId)}
    >
      {modules.map((module) => (
        <ModuleSquare key={module.slug} {...module} density={density} onNavigate={onNavigate} />
      ))}
    </ul>
  );
}
